import '../styles/Timeline.scss';

import { Stack } from '@whistler-ui/k2/components/Stack';
import { Heading, Paragraph } from '@whistler-ui/k2/components/Typography';
import { useClassHelper } from '@whistler-ui/k2/hooks';

// This is commented cuz this comment: https://netlync.atlassian.net/browse/UES-7835?focusedCommentId=20128
// But i dont want to remove it because it may be useful in the future
// import TIMELINE_SVG from '../../../assets/common/timeline.svg';
// import CSG_TIMELINE_SVG from '../../../assets/csg/timeline.svg';
import { TIMELINE_DATES } from '../../../constants';
import { useRenderIf } from '../../../hooks/useRenderIf';

type PhaseStepsProps = {
  phase: {
    phase: number;
    status?: string;
    steps?: {
      title: string;
      date: string;
    }[];
  };
};

const componentName = 'p-Timeline';

const PhaseSteps = ({ phase }: PhaseStepsProps) => {
  const c = useClassHelper(componentName);

  if (phase.status === 'CLOSED') {
    return (
      <Stack direction="column" gap={4} tag="ul">
        <li>
          <Paragraph>
            <strong>Closed</strong>
          </Paragraph>
          <Paragraph className={c('__date')}>This phase is closed</Paragraph>
        </li>
      </Stack>
    );
  }

  return (
    <Stack direction="column" gap={4} tag="ul">
      {phase.steps.map((step, index) => (
        <li key={index}>
          <Paragraph>
            <strong>{step.title}</strong>
          </Paragraph>
          <Paragraph className={c('__date')}>{step.date}</Paragraph>
        </li>
      ))}
    </Stack>
  );
};

export function Timeline() {
  const { RenderIf } = useRenderIf();
  const c = useClassHelper(componentName);

  const phasesHeaders = TIMELINE_DATES.ues.map(phase => `Phase ${phase.phase}`);
  const partnerPhasesHeaders = TIMELINE_DATES.partner.map(
    phase => `Phase ${phase.phase}`,
  );

  return (
    <Stack tag="article" direction="row" gap={8} className={componentName}>
      <Stack direction="column" gap={8}>
        <Heading>Timeline</Heading>
        <Stack direction="column" gap={4}>
          <RenderIf ues={true}>
            <Paragraph>
              The upcoming iOS Go-Live phases are listed in the table below.{' '}
              <br />
              For Android, timing is set directly by the OEM and doesn't follow
              predefined release phases. You can express your interest via the
              &nbsp;
              <a href="https://www.gsmaservices.com/product/entitlements/android-launches-android-network-ready-program-for-premium-mobile-services/">
                Android Network Ready program
              </a>
              .
            </Paragraph>
            <table className={c('__timeline-table')}>
              <thead>
                <tr>
                  {phasesHeaders.map((header, index) => (
                    <th key={index}>
                      <Heading level={4}>{header}</Heading>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  {TIMELINE_DATES.ues.map((phase, index) => (
                    <td key={index}>
                      <PhaseSteps phase={phase} />
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
            <Paragraph>
              To be included in a Go-Live phase, we'll need all Production
              Readiness activities to be completed by the phase deadline. This
              includes uploading your ES settings to GSMA NSX and completing
              both Baseline and eSIM Transfer, which are required use cases for
              iOS.
            </Paragraph>
            <Paragraph>
              <b>
                Let us know which Go-Live phase you're targeting so we can help
                you achieve it!
              </b>
              <br />
              If you run out of time or your certification testing overruns,
              you'll automatically roll into the next phase.
            </Paragraph>
            <Paragraph>
              <strong>Please Note:</strong> eligibility and go-live timing is at
              the sole discretion of the OEM. Phase 7's deadline is earlier to
              accommodate your Christmas freeze, while phase 8's deadline is
              advanced to align with the typical major OEM release in September.
            </Paragraph>
          </RenderIf>

          <RenderIf ues={false}>
            <Paragraph>
              As an Apple partner your Apple carrier team representative can
              work with you to align on timelines for Apple certification
              testing and target launch windows.
            </Paragraph>
            <Paragraph>
              Indicative iOS Go-Live phases are listed in the table below.{' '}
              <br />
              For Android, timing is set directly by the OEM and doesn't follow
              predefined release phases. You can express your interest via the
              &nbsp;
              <a href="https://www.gsmaservices.com/product/entitlements/android-launches-android-network-ready-program-for-premium-mobile-services/">
                Android Network Ready program
              </a>
              .
            </Paragraph>
            <table className={c('__timeline-table')}>
              <thead>
                <tr>
                  {partnerPhasesHeaders.map((header, index) => (
                    <th key={index}>
                      <Heading level={4}>{header}</Heading>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  {TIMELINE_DATES.partner.map((phase, index) => (
                    <td key={index}>
                      <PhaseSteps phase={phase} />
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
            <Paragraph>
              <b>
                Once you have your target dates confirmed, please let us know so
                we can help you achieve them.
              </b>
            </Paragraph>
            <Paragraph>
              <strong>Please Note:</strong> eligibility and go-live timing is at
              the sole discretion of the OEM.
            </Paragraph>
          </RenderIf>
        </Stack>
      </Stack>
      {/* <RenderIf ues={true}>
        <aside>
          <img
            src={isCSG ? CSG_TIMELINE_SVG : TIMELINE_SVG}
            alt="timeline clock"
            className={c('__image')}
          />
        </aside>
      </RenderIf> */}
    </Stack>
  );
}

export default Timeline;
